<template>
    <span v-if="!session.is_completed">
        {{ $t("virtual_visit:start_a_call:description") }}:
        <a class="btn btn-default btn-xs" @click="openSMS">{{ $t("virtual_visit:send_sms:btn") }}</a>
        {{ $t("general:or") }}
        <a class="btn btn-default btn-xs" @click="openEmail">{{ $t("virtual_visit:send_email:btn") }}</a>.
        {{ $t("virtual_visit:sms_email_verification:description") }}

        <modal
            :name="smsModalName"
            :scrollable="true"
            height="auto"
            width="500px"
            :classes="['ivc-modal']"
            @before-close="onModalClose"
        >
            <div class="modal-content">
                <div class="modal-header">
                    <button type="button" class="close" @click="$modal.hide(smsModalName)">&times;</button>
                    <h4 class="modal-title">{{ $t("virtual_visit:send_sms:btn") }}</h4>
                </div>

                <div class="modal-body">
                    <p>{{ $t("virtual_visit:send_sms:description") }}</p>

                    <div class="form-group">
                        <label for="ivc-sms-mobile">{{ $t("virtual_visit:phone_number") }}</label>
                        <input
                            id="ivc-sms-mobile"
                            v-model.trim="smsMobile"
                            type="tel"
                            class="form-control"
                            placeholder="+38631456789"
                            autocomplete="tel"
                        />
                    </div>

                    <div class="form-group">
                        <div class="ivc-preview-label-row">
                            <label class="ivc-preview-label">{{ $t("virtual_visit:message_preview") }}</label>
                            <span class="ivc-lang">
                                <button ref="smsChip" type="button" class="ivc-lang__chip" :title="$t('virtual_visit:language')" @click.stop="toggleLang('sms')">
                                    <span class="ivc-lang__flag">{{ langFlag(smsLanguage) }}</span>
                                    <span class="ivc-lang__code">{{ langCode(smsLanguage) }}</span>
                                    <i class="fa fa-caret-down"></i>
                                </button>
                                <ul v-if="langOpen === 'sms'" class="ivc-lang__menu" :style="menuStyle">
                                    <li v-for="l in languages" :key="l.code">
                                        <button type="button" :class="['ivc-lang__option', { 'is-active': l.code === smsLanguage }]" @click="selectLang('sms', l.code)">
                                            <span class="ivc-lang__flag">{{ l.flag }}</span>
                                            <span>{{ l.label }}</span>
                                        </button>
                                    </li>
                                </ul>
                            </span>
                        </div>
                        <textarea
                            class="form-control ivc-body-textarea"
                            rows="5"
                            readonly
                            :value="smsBody"
                        ></textarea>
                    </div>

                    <div class="text-right">
                        <button type="button" class="btn btn-default" @click="$modal.hide(smsModalName)">
                            {{ $t("general:cancel:btn") }}
                        </button>
                        <button type="button" class="btn btn-info" :disabled="smsSending || !smsMobile" @click="sendSMS">
                            {{ $t("general:send:btn") }}
                        </button>
                    </div>
                </div>
            </div>
        </modal>

        <modal
            :name="emailModalName"
            :scrollable="true"
            height="auto"
            width="550px"
            :classes="['ivc-modal']"
            @before-close="onModalClose"
        >
            <div class="modal-content">
                <div class="modal-header">
                    <button type="button" class="close" @click="$modal.hide(emailModalName)">&times;</button>
                    <h4 class="modal-title">{{ $t("virtual_visit:send_email:btn") }}</h4>
                </div>

                <div class="modal-body">
                    <p>{{ $t("virtual_visit:send_email:description") }}</p>

                    <div class="form-group">
                        <div class="ivc-preview-label-row">
                            <label class="ivc-preview-label">{{ $t("virtual_visit:message_preview") }}</label>
                            <span class="ivc-lang">
                                <button ref="emailChip" type="button" class="ivc-lang__chip" :title="$t('virtual_visit:language')" @click.stop="toggleLang('email')">
                                    <span class="ivc-lang__flag">{{ langFlag(emailLanguage) }}</span>
                                    <span class="ivc-lang__code">{{ langCode(emailLanguage) }}</span>
                                    <i class="fa fa-caret-down"></i>
                                </button>
                                <ul v-if="langOpen === 'email'" class="ivc-lang__menu" :style="menuStyle">
                                    <li v-for="l in languages" :key="l.code">
                                        <button type="button" :class="['ivc-lang__option', { 'is-active': l.code === emailLanguage }]" @click="selectLang('email', l.code)">
                                            <span class="ivc-lang__flag">{{ l.flag }}</span>
                                            <span>{{ l.label }}</span>
                                        </button>
                                    </li>
                                </ul>
                            </span>
                        </div>
                        <textarea
                            class="form-control ivc-body-textarea"
                            rows="8"
                            readonly
                            :value="emailBody"
                        ></textarea>
                    </div>

                    <div class="text-right">
                        <button type="button" class="btn btn-default" @click="$modal.hide(emailModalName)">
                            {{ $t("general:close") }}
                        </button>
                        <button type="button" :class="['btn', emailCopied ? 'btn-success' : 'btn-info']" @click="copyEmail">
                            <span v-if="emailCopied"><i class="fa fa-check"></i> {{ $t("general:copied") }}</span>
                            <span v-else><i class="fa fa-copy"></i> {{ $t("general:copy") }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </modal>
    </span>
</template>

<script>
import ApiService from '@/components/utils/api.service.js';

const LANGUAGES = [
    { code: "sl-SI", flag: "🇸🇮", label: "Slovenščina" },
    { code: "hr",    flag: "🇭🇷", label: "Hrvatski" },
    { code: "en",    flag: "🇬🇧", label: "English" },
    { code: "cs",    flag: "🇨🇿", label: "Čeština" },
    { code: "de",    flag: "🇩🇪", label: "Deutsch" },
    { code: "hu",    flag: "🇭🇺", label: "Magyar" },
    { code: "ro",    flag: "🇷🇴", label: "Română" },
];

const UI_LOCALE_TO_LANG = {
    sl: "sl-SI", "sl-SI": "sl-SI",
    hr: "hr", en: "en", cs: "cs", de: "de", hu: "hu", ro: "ro",
};

let _instanceId = 0;

export default {
    props: {
        session: { type: Object, required: true },
        teamLanguage: { type: String, default: "" },
    },
    data() {
        const uiLocale = (this.$i18n && this.$i18n.locale) || "sl";
        // The moderator's team language is the default; fall back to the UI locale.
        const defaultLang = UI_LOCALE_TO_LANG[this.teamLanguage]
            || UI_LOCALE_TO_LANG[uiLocale]
            || "sl-SI";
        const id = ++_instanceId;
        return {
            languages: LANGUAGES,
            smsModalName: `ivc-sms-${id}`,
            emailModalName: `ivc-email-${id}`,
            smsLanguage: defaultLang,
            emailLanguage: defaultLang,
            smsMobile: "",
            smsSending: false,
            emailCopied: false,
            langOpen: null,
            langMenuPos: null,
            _outsideHandler: null,
            _windowHandler: null,
            _copiedTimer: null,
        };
    },
    computed: {
        clientUrl() {
            return this.session.client_url_full;
        },
        smsBody() {
            const template = this.$i18n.t("virtual_visit:allow_camera_access", this.vueLocale(this.smsLanguage));
            const url = this.clientUrl ? `${this.clientUrl}?lang=${this.smsLanguage}` : "";
            return template.replace(/%s%s/, url);
        },
        emailBody() {
            const locale = this.vueLocale(this.emailLanguage);
            const content = this.$i18n.t("virtual_visit:send_email:content", locale);
            const greeting = this.$i18n.t("virtual_visit:send_email:greeting", locale);
            const url = this.clientUrl ? `${this.clientUrl}?lang=${this.emailLanguage}` : "";
            return `${content}\n\n${url}\n\n${greeting}.`;
        },
        menuStyle() {
            if (!this.langMenuPos) {
                return { position: "fixed", visibility: "hidden" };
            }
            return {
                position: "fixed",
                top: this.langMenuPos.top + "px",
                right: this.langMenuPos.right + "px",
            };
        },
    },
    methods: {
        vueLocale(code) {
            return code === "sl-SI" ? "sl" : code;
        },
        langFlag(code) {
            const l = this.languages.find((x) => x.code === code);
            return l ? l.flag : "";
        },
        langCode(code) {
            return (code || "").split("-")[0].toUpperCase();
        },
        openSMS() {
            this.langOpen = null;
            this.$modal.show(this.smsModalName);
        },
        openEmail() {
            this.langOpen = null;
            this.emailCopied = false;
            this.$modal.show(this.emailModalName);
        },
        toggleLang(which) {
            if (this.langOpen === which) {
                this.langOpen = null;
                this.langMenuPos = null;
                return;
            }
            this.langOpen = which;
            this.langMenuPos = null;
            this.$nextTick(() => this.updateMenuPos());
        },
        updateMenuPos() {
            if (!this.langOpen) return;
            const ref = this.langOpen === "sms" ? this.$refs.smsChip : this.$refs.emailChip;
            if (!ref) return;
            const r = ref.getBoundingClientRect();
            this.langMenuPos = {
                top: Math.round(r.bottom + 4),
                right: Math.round(window.innerWidth - r.right),
            };
        },
        selectLang(which, code) {
            if (which === "sms") this.smsLanguage = code;
            else this.emailLanguage = code;
            this.langOpen = null;
            this.langMenuPos = null;
        },
        onModalClose() {
            this.langOpen = null;
            this.langMenuPos = null;
        },
        sendSMS() {
            if (!this.smsMobile || this.smsSending) return;
            this.smsSending = true;
            ApiService
                .post(`/virtual-visit/api/virtual-session/${this.session.id}/send-invite-client-sms`, {
                    mobile: this.smsMobile,
                    language: this.smsLanguage,
                })
                .then((response) => {
                    this.smsSending = false;
                    if (response.data.status === "FAILED") {
                        this.notify(this.$t("virtual_visit:send_sms:not_sent"), this.$t("virtual_visit:send_sms:error:general"), "warn");
                    } else if (response.data.status === "INVALID") {
                        this.notify(this.$t("virtual_visit:send_sms:not_sent"), this.$t("virtual_visit:send_sms:error:invalid_number"), "warn");
                    } else {
                        this.notify(this.$t("virtual_visit:sms_sent"), "", "success");
                        this.$modal.hide(this.smsModalName);
                    }
                })
                .catch((error) => {
                    this.smsSending = false;
                    let msg = this.$t("virtual_visit:send_sms:error:general");
                    if (error.response && error.response.status === 403) {
                        msg = this.$t("virtual_visit:send_sms:error:no_permissions");
                    } else if (error.response && error.response.status === 400) {
                        msg = this.$t("virtual_visit:send_sms:error:invalid_number");
                    }
                    this.notify(this.$t("virtual_visit:send_sms:not_sent"), msg, "warn");
                });
        },
        copyEmail() {
            const text = this.emailBody;
            const showCopied = () => {
                this.emailCopied = true;
                if (this._copiedTimer) clearTimeout(this._copiedTimer);
                this._copiedTimer = setTimeout(() => { this.emailCopied = false; }, 1500);
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(showCopied).catch(() => this._fallbackCopy(text, showCopied));
            } else {
                this._fallbackCopy(text, showCopied);
            }
        },
        _fallbackCopy(text, onSuccess) {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.style.position = "fixed";
            ta.style.top = "-1000px";
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            try {
                document.execCommand("copy");
                onSuccess();
            } catch (e) {
                // noop
            }
            document.body.removeChild(ta);
        },
        notify(title, text, type) {
            this.$notify({ group: "OTwarnings", type: type, title: title, text: text });
        },
    },
    mounted() {
        this._outsideHandler = (e) => {
            if (this.langOpen && !e.target.closest(".ivc-lang") && !e.target.closest(".ivc-lang__menu")) {
                this.langOpen = null;
                this.langMenuPos = null;
            }
        };
        this._windowHandler = () => {
            if (this.langOpen) {
                this.langOpen = null;
                this.langMenuPos = null;
            }
        };
        document.addEventListener("click", this._outsideHandler);
        window.addEventListener("resize", this._windowHandler);
    },
    beforeDestroy() {
        if (this._outsideHandler) {
            document.removeEventListener("click", this._outsideHandler);
        }
        if (this._windowHandler) {
            window.removeEventListener("resize", this._windowHandler);
        }
        if (this._copiedTimer) clearTimeout(this._copiedTimer);
    },
};
</script>

<style scoped>
.ivc-preview-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5em;
    margin-bottom: 5px;
}
.ivc-preview-label {
    margin: 0;
}
.ivc-lang {
    position: relative;
    display: inline-block;
}
.ivc-lang__chip {
    background: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 999px;
    padding: 0.2em 0.7em;
    font-size: 0.95em;
    line-height: 1.2;
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    cursor: pointer;
}
.ivc-lang__chip:hover {
    background: #ececec;
}
.ivc-lang__flag {
    font-size: 1.1em;
    line-height: 1;
}
.ivc-lang__code {
    font-weight: 600;
    letter-spacing: 0.05em;
}
.ivc-lang__menu {
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    list-style: none;
    margin: 0;
    padding: 4px 0;
    min-width: 180px;
    z-index: 10000;
}
.ivc-lang__option {
    background: none;
    border: 0;
    width: 100%;
    text-align: left;
    padding: 6px 12px;
    display: flex;
    align-items: center;
    gap: 0.5em;
    cursor: pointer;
    color: inherit;
    font: inherit;
}
.ivc-lang__option:hover {
    background: #f5f5f5;
}
.ivc-lang__option.is-active {
    font-weight: 600;
}
.ivc-body-textarea {
    text-align: left;
    font-family: inherit;
    resize: vertical;
    background: #fafafa;
    white-space: pre-wrap;
}
.text-right .btn + .btn {
    margin-left: 6px;
}
</style>
